import React, { useState, useCallback, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import {
  canAccessAccounts,
  canAccessPortalBranding,
  END_USER_HIDDEN_NAV_IDS,
} from './lib/accountRoles';
import { ThemeProvider } from './contexts/ThemeContext';
import { CustomerHistoryProvider } from './contexts/CustomerHistoryContext';
import { RealTimeEventsProvider } from './contexts/RealTimeEventsContext';
import { Layout } from './components/Layout';
import { Navigation } from './components/Navigation';
import { Toaster } from './components/ui/sonner';
import { ErrorBoundary } from './components/ErrorBoundary';


// Lazy load pages for better performance and code splitting
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const RegistrationPage = React.lazy(() => import('./pages/RegistrationPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const RoutersPage = React.lazy(() => import('./pages/RoutersPage'));
const CustomersPage = React.lazy(() => import('./pages/CustomersPage'));
const ServicePlansPage = React.lazy(() => import('./pages/ServicePlansPage'));
const VouchersPage = React.lazy(() => import('./pages/VouchersPage'));
const SupportTicketsPage = React.lazy(() => import('./pages/SupportTicketsPage'));
const AccountsPage = React.lazy(() => import('./pages/AccountsPage'));
const ConsumptionHistoryPage = React.lazy(() => import('./pages/ConsumptionHistoryPage'));
const VoucherBatchDetailsPage = React.lazy(() => import('./pages/VoucherBatchDetailsPage'));
const RouterTerminalPage = React.lazy(() => import('./pages/RouterTerminalPage'));
const VoucherWordPreviewPage = React.lazy(() => import('./pages/VoucherWordPreviewPage'));
const PortalBrandingPage = React.lazy(() => import('./pages/PortalBrandingPage'));
const FirmwarePage = React.lazy(() => import('./pages/FirmwarePage'));
const FaqPage = React.lazy(() => import('./pages/FaqPage'));

// Loading fallback component
const PageLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading page...</p>
    </div>
  </div>
);

// Memoized AppContent component for better performance
const AppContent = React.memo(() => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [savedScrollPositions, setSavedScrollPositions] = useState<Record<string, number>>({});
  const [isReturningFromConsumptionHistory, setIsReturningFromConsumptionHistory] = useState(false);
  const [selectedVoucherId, setSelectedVoucherId] = useState<string | null>(null);
  const [authView, setAuthView] = useState<'login' | 'register' | 'forgot'>('login');

  // Get current page from location
  const currentPage = location.pathname.split('/')[1] || 'dashboard';

  const handlePageChange = useCallback((page: string, preserveScroll?: boolean, voucherId?: string) => {
    try {
      // Save current scroll position if navigating away from customers page
      if (currentPage === 'customers' && page === 'consumption-history') {
        setSavedScrollPositions(prev => ({
          ...prev,
          customers: window.scrollY
        }));
        setIsReturningFromConsumptionHistory(false);
      }
      
      // Save current scroll position if navigating away from vouchers page
      if (currentPage === 'vouchers' && page === 'voucher-batch-details') {
        setSavedScrollPositions(prev => ({
          ...prev,
          vouchers: window.scrollY
        }));
        setSelectedVoucherId(voucherId || null);
      }
      
      // Set flag when returning from consumption history
      if (currentPage === 'consumption-history' && page === 'customers') {
        setIsReturningFromConsumptionHistory(true);
      }
      
      navigate(`/${page}`);
      
      // Restore scroll position if returning to customers page
      if (page === 'customers' && preserveScroll && savedScrollPositions.customers !== undefined) {
        setTimeout(() => {
          window.scrollTo(0, savedScrollPositions.customers);
        }, 0);
      }
      
      // Restore scroll position if returning to vouchers page
      if (page === 'vouchers' && preserveScroll && savedScrollPositions.vouchers !== undefined) {
        setTimeout(() => {
          window.scrollTo(0, savedScrollPositions.vouchers);
        }, 0);
      }
    } catch (error) {
      console.error('Page change error:', error);
      navigate('/dashboard');
    }
  }, [currentPage, savedScrollPositions, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const endUserBlockedPage =
    user?.role === 'end_user' &&
    END_USER_HIDDEN_NAV_IDS.includes(currentPage as (typeof END_USER_HIDDEN_NAV_IDS)[number]);

  if (!user) {
    return (
      <Suspense fallback={<PageLoadingFallback />}>
        {authView === 'register' ? (
          <RegistrationPage onBackToLogin={() => setAuthView('login')} />
        ) : authView === 'forgot' ? (
          <ForgotPasswordPage onBackToLogin={() => setAuthView('login')} />
        ) : (
          <LoginPage
            onGoToRegister={() => setAuthView('register')}
            onGoToForgotPassword={() => setAuthView('forgot')}
          />
        )}
      </Suspense>
    );
  }

  if (endUserBlockedPage) {
    return <Navigate to="/dashboard" replace />;
  }

  // Full-screen standalone pages (no layout wrapper)
  if (location.pathname.match(/^\/routers\/[^/]+\/terminal$/) || location.pathname === '/vouchers/word-preview') {
    return (
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          <Route path="/routers/:routerId/terminal" element={<RouterTerminalPage />} />
          <Route path="/vouchers/word-preview" element={<VoucherWordPreviewPage />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Layout noPadding={currentPage === 'consumption-history' || currentPage === 'voucher-batch-details'}>
      {/* Only show Navigation sidebar for pages that need it */}
      {currentPage !== 'consumption-history' && currentPage !== 'voucher-batch-details' && (
        <Navigation currentPage={currentPage} onPageChange={handlePageChange} />
      )}
      
      <Suspense fallback={<PageLoadingFallback />}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage onPageChange={handlePageChange} />} />
          <Route path="/routers" element={
            <RoutersPage onPageChange={handlePageChange} />
          } />
          <Route path="/customers" element={<CustomersPage onPageChange={handlePageChange} />} />
          <Route path="/consumption-history" element={
            <ConsumptionHistoryPage 
              onBack={() => handlePageChange('customers', true)} 
              shouldScrollToTop={!isReturningFromConsumptionHistory}
            />
          } />
          <Route path="/service-plans" element={<ServicePlansPage />} />
          <Route path="/vouchers" element={<VouchersPage onPageChange={handlePageChange} />} />
          <Route path="/voucher-batch-details" element={
            <VoucherBatchDetailsPage 
              voucherId={selectedVoucherId}
              onBack={() => handlePageChange('vouchers', true)} 
              shouldScrollToTop={true}
            />
          } />
          <Route path="/support-tickets" element={<SupportTicketsPage />} />
          <Route path="/accounts" element={
            canAccessAccounts(user?.role, user?.parentAccountId)
              ? <AccountsPage />
              : <Navigate to="/dashboard" replace />
          } />
          <Route path="/portal-branding" element={
            canAccessPortalBranding(user?.role, user?.parentAccountId)
              ? <PortalBrandingPage />
              : <Navigate to="/dashboard" replace />
          } />
          <Route path="/firmware" element={<FirmwarePage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  );
});

AppContent.displayName = 'AppContent';

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <RealTimeEventsProvider>
              <CustomerHistoryProvider>
                <AppContent />
                <Toaster />
              </CustomerHistoryProvider>
            </RealTimeEventsProvider>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}